import { MutableRefObject, useCallback, useRef, useState } from 'react'
import { isEqual } from 'lodash-es'
import dayjs from 'dayjs'
import {
  ReturnManagementItemRes,
  ReturnManagementRow,
  ReturnManagementWriteBody,
  SaveReturnManagementReq,
  toEditableRows,
  toWriteBody,
} from '@/services/return/returnManagement/types'

/** 저장 시 필수값 — 표 헤더 별표(4.3)와 같은 출처. 별표는 붙는데 검사가 없으면 BE 400 으로 튕긴다. */
const REQUIRED_FIELDS: { key: keyof ReturnManagementRow; title: string }[] = [
  { key: 'plnDate', title: '반품일자' },
  { key: 'ordQty', title: '수량' },
  { key: 'trsNm', title: '수하인' },
  { key: 'trsAddr', title: '주소' },
  { key: 'trsInvoiceNo', title: '반송번호' },
]

// `isBlank(0)` 은 false 다 — 의도적이다. 계획서 3.2(BE 코드 실측)는 ordQty 를 "필수, null 불가"로만
// 적는다. "0 불가"는 문서·코드 어디에도 없어, 검증되지 않은 채 클라이언트가 더 엄격한 규칙을
// 만들어 붙이면(D8 과 같은 취지) 유효할 수 있는 저장을 막을 위험이 있다(리뷰 반영).
const isBlank = (value: unknown) => value == null || (typeof value === 'string' && value.trim() === '')

export interface MissingRequiredCell {
  rowKey: string
  title: string
}

/**
 * 빈 신규 행 템플릿 — 훅 바깥의 모듈 스코프 함수다. `seqRef`(ref)를 인자로 받아 훅 렌더마다
 * 새로 만들어지는 클로저가 아니게 하고, `addRow`·`addRowsFromImport`의 `useCallback` 이
 * `eslint-disable` 없이 deps 를 그대로 채울 수 있게 한다. `overrides` 로 엑셀 업로드 값이나
 * 조회에 쓴 반품처 기본값을 얹을 수 있다(9단계, LLD 5장 4단계).
 */
const buildEmptyRow = (seqRef: MutableRefObject<number>, overrides: Partial<ReturnManagementRow> = {}): ReturnManagementRow => {
  seqRef.current += 1
  return {
    id: 0,
    shipperName: null,
    sprNm: null,
    ordNo: null,
    ordOptNo: null,
    rsnCode: null,
    rtnType: null,
    rlChannel: null,
    barcode: null,
    skuId: null,
    brdNm: null,
    gdsCd: null,
    gdsNm: null,
    gdsOpt: null,
    ordQty: null,
    trsNm: null,
    trsPhone: null,
    trsAddr: null,
    trsNumber: null,
    trsInvoiceNo: null,
    trsLspNm: null,
    salChannel: null,
    procNm: null,
    refNo1: null,
    refNo2: null,
    refNo3: null,
    etc1: null,
    etc2: null,
    etc3: null,
    etc4: null,
    etc5: null,
    flag: '',
    apiFlag: null,
    jobCommentForInvoice: null,
    claimId: null,
    insDate: null,
    insBy: null,
    updDate: null,
    updBy: null,
    fileId: null,
    plnDate: dayjs().format('YYYY-MM-DD'),
    isNew: true,
    atchFileCount: 0,
    videoCount: 0,
    ...overrides,
    rowKey: `new-${seqRef.current}`,
  }
}

/**
 * RGR04 편집 행 상태 — 계획서 5장 구현 단계 4.
 *
 * 조회 결과를 편집 가능한 행으로 들고, 원본 스냅샷과 비교해 실제로 바뀐 행만 `updates` 로 골라낸다.
 * 행 추가는 `creates` 로 간다. 신규 행(추가·엑셀 업로드)은 **맨 위**에 넣는다 — 데모 [+ 추가](tableAppendRow)와
 * 같은 계약이고(PD 확정 2026-09-30), 상품관리(ma02) 배선과 같다.
 *
 * 저장은 **고른 행**만 대상이다 — 데모 [저장]은 선택이 있어야 눌린다(ma02 와 같은 규약). 삭제는 확인창에서 바로 서버에 반영하므로
 * (배선이 `deleteIds` 만 담은 저장 요청을 즉시 보낸다) 여기서는 화면에서 빼기만 한다 — 삭제를 다음 저장까지 미뤄 두면 삭제 뒤
 * 선택이 비어 [저장]을 누를 수 없어 삭제가 영영 나가지 않는다.
 */
export const useReturnManagementRows = () => {
  const [rows, setRows] = useState<ReturnManagementRow[]>([])
  // id → 조회 시점 원본. 저장 대상 판정(변경 여부)의 기준이다. 재조회·저장 성공 시 다시 채운다.
  const originalByIdRef = useRef<Map<number, ReturnManagementItemRes>>(new Map())
  const newRowSeqRef = useRef(0)

  const setRowsFromServer = useCallback((items: ReturnManagementItemRes[]) => {
    originalByIdRef.current = new Map(items.map((item) => [item.id, item]))
    setRows(toEditableRows(items))
  }, [])

  /**
   * 재조회 결과 위에 **아직 저장하지 않은 편집**을 다시 얹는다 — 저장·삭제·확정 뒤 재조회가 고르지 않은 행의 편집과
   * 미저장 신규 행을 지우지 않게 한다. 신규 행은 맨 위에, 저장된 행은 같은 id 자리에 되돌린다(서버에서 사라진 id 는 버린다).
   * 원본 스냅샷은 방금 받은 서버 값이라 되돌린 행은 계속 "바뀐 행" 으로 남아 다음 저장 대상이 된다.
   */
  const overlayRows = useCallback((pending: ReturnManagementRow[]) => {
    if (pending.length === 0) return
    const newRows = pending.filter((r) => r.isNew)
    const editedById = new Map(pending.filter((r) => !r.isNew).map((r) => [r.id, r]))
    setRows((prev) => [...newRows, ...prev.map((r) => editedById.get(r.id) ?? r)])
  }, [])

  /**
   * skuId 는 조회 응답에서만 채워지는 읽기 전용 칸이다. 바코드가 원본과 다르면 옛 바코드의 skuId 가
   * 남지 않게 비우고, 원본 바코드로 되돌리면 원본 skuId 를 되살린다. 신규 행은 원본이 없어 그대로 둔다.
   */
  const onRowsChange = useCallback((changed: ReturnManagementRow[]) => {
    setRows(
      changed.map((r) => {
        const original = r.isNew ? undefined : originalByIdRef.current.get(r.id)
        if (!original) return r
        const skuId = r.barcode === original.barcode ? original.skuId : null
        return r.skuId === skuId ? r : { ...r, skuId }
      }),
    )
  }, [])

  /** 반품일자를 오늘로 채운 빈 행을 **맨 위에** 추가한다(계획서 5장 4단계 · 데모 tableAppendRow). id 는 저장 전까지 0(미저장 표식). */
  const addRow = useCallback((overrides: Partial<ReturnManagementRow> = {}) => {
    const empty = buildEmptyRow(newRowSeqRef, overrides)
    setRows((prev) => [empty, ...prev])
    return empty.rowKey
  }, [])

  /**
   * 엑셀 업로드 파싱 결과를 전부 신규 행으로 넣는다(9단계, 피드백 6).
   * 반품번호로 기존 행을 찾아 수정하지 않는다 — 파싱 값을 그대로 새 행에 얹는다.
   *
   * `overrides` 는 파싱 값보다 뒤에 얹는다. 파일이 아니라 화면이 정하는 값(화주코드)을 담는 자리라,
   * 파일에 같은 칸이 들어 있어도 화면 값이 이긴다 — 저장은 요청 단위 화주로 나가므로 파일 값을
   * 그대로 보여 주면 실제 저장되는 화주와 화면이 어긋난다.
   */
  const addRowsFromImport = useCallback((items: Record<string, unknown>[], overrides: Partial<ReturnManagementRow> = {}) => {
    const imported = items.map((item) => buildEmptyRow(newRowSeqRef, { ...(item as Partial<ReturnManagementRow>), ...overrides }))
    // 파일 순서는 지키고 묶음째 맨 위에 — [추가]와 같은 자리(검토·선택·저장할 행이 위에 모인다, ma02 와 같다).
    setRows((prev) => [...imported, ...prev])
    return imported.length
  }, [])

  /**
   * 행을 화면에서 뺀다. 저장된 행의 서버 삭제(`deleteIds`)는 배선이 확인창에서 **먼저** 보내고, 성공한 뒤에만 이걸 부른다
   * (요청 전에 상태를 비우지 않는다 — 가이드 52 규칙 11). 미저장 행(`isNew`)은 서버에 없어 이것만으로 끝이다(피드백 9).
   */
  const deleteRows = useCallback((rowKeys: string[]) => {
    const targets = new Set(rowKeys)
    setRows((prev) => prev.filter((r) => !targets.has(r.rowKey)))
  }, [])

  /** 원본과 비교해 실제로 바뀐 값이 있는지 — `toWriteBody` 로 두 값 다 정규화한 뒤 비교한다. */
  const hasChanged = (row: ReturnManagementRow): boolean => {
    const original = originalByIdRef.current.get(row.id)
    if (!original) return true
    const [originalRow] = toEditableRows([original])
    return !isEqual(toWriteBody(row), toWriteBody(originalRow))
  }

  /**
   * 저장 대상(신규 + 실제로 바뀐 행)만 모은다. 필수값 검사·저장 payload 조립이 이 목록을 공유한다.
   * `rowKeys` 를 주면 그 행들로 좁힌다(데모 [저장] = 고른 행). 주지 않으면 전체다.
   */
  const getSaveTargets = useCallback(
    (rowKeys?: ReadonlySet<string>) => rows.filter((r) => (!rowKeys || rowKeys.has(r.rowKey)) && (r.isNew || hasChanged(r))),
    // hasChanged 는 렌더마다 새로 만들어지지만 originalByIdRef(ref — 항상 최신)만 읽는다. deps 에 넣으면 getSaveTargets 가
    //  매 렌더 새로 만들어져 이를 deps 로 쓰는 findMissingRequired·buildSavePayload·getPendingRows 까지 매번 바뀐다 — rows 만 보면 충분하다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows],
  )

  /** 아직 저장하지 않은 편집(신규 + 바뀐 행) 중 `except` 에 없는 것 — 저장·삭제·확정 뒤 재조회에 다시 얹는다(overlayRows). */
  const getPendingRows = useCallback(
    (except: ReadonlySet<string> = new Set()) => getSaveTargets().filter((r) => !except.has(r.rowKey)),
    [getSaveTargets],
  )

  const findMissingRequired = useCallback(
    (rowKeys?: ReadonlySet<string>): MissingRequiredCell | undefined => {
      return getSaveTargets(rowKeys)
        .map((row) => {
          const missing = REQUIRED_FIELDS.find((f) => isBlank(row[f.key]))
          return missing ? { rowKey: row.rowKey, title: missing.title } : undefined
        })
        .find(Boolean)
    },
    [getSaveTargets],
  )

  /**
   * 저장 요청 본문(`rowKeys` 로 좁힌 신규·변경 행). 대상이 없으면 null — 빈 creates/updates/deleteIds 는 서버가 400 을 준다(3.2).
   * 삭제는 여기 싣지 않는다 — 확인창에서 즉시 보낸다(buildDeletePayload).
   */
  const buildSavePayload = useCallback(
    (shipper: string, rowKeys?: ReadonlySet<string>): SaveReturnManagementReq | null => {
      const targets = getSaveTargets(rowKeys)
      const creates: ReturnManagementWriteBody[] = targets.filter((r) => r.isNew).map(toWriteBody)
      // id 로 dedupe — 같은 행이 두 번 잡혀도(이론상 없어야 하지만) 서버 400(id 중복)을 만들지 않는다.
      const updatesById = new Map(targets.filter((r) => !r.isNew).map((r) => [r.id, { ...toWriteBody(r), id: r.id }]))

      if (creates.length === 0 && updatesById.size === 0) return null

      return { shipper, creates, updates: [...updatesById.values()], deleteIds: [] }
    },
    [getSaveTargets],
  )

  /** 삭제 요청 본문 — 고른 행 중 **저장된** 행의 id 만. 미저장 행뿐이면 null(서버에 보낼 것이 없다, 화면에서만 뺀다). */
  const buildDeletePayload = useCallback(
    (shipper: string, rowKeys: ReadonlySet<string>): SaveReturnManagementReq | null => {
      const ids = [...new Set(rows.filter((r) => rowKeys.has(r.rowKey) && !r.isNew).map((r) => r.id))]
      return ids.length ? { shipper, creates: [], updates: [], deleteIds: ids } : null
    },
    [rows],
  )

  /** 첨부·동영상 개수(4.5)를 `fileId` 로 매칭해 셀 값에 얹는다. 편집·저장 판정과는 무관한 표시값이다. */
  const applyMediaCounts = useCallback((counts: Map<string, { atchFileCount: number; videoCount: number }>) => {
    if (counts.size === 0) return
    setRows((prev) =>
      prev.map((r) => {
        const c = r.fileId ? counts.get(r.fileId) : undefined
        return c ? { ...r, ...c } : r
      }),
    )
  }, [])

  return {
    rows,
    setRowsFromServer,
    overlayRows,
    onRowsChange,
    addRow,
    addRowsFromImport,
    deleteRows,
    getPendingRows,
    findMissingRequired,
    buildSavePayload,
    buildDeletePayload,
    applyMediaCounts,
  }
}
